import dayjs from 'dayjs';
import type { FormikErrors } from 'formik';
import { Permission } from 'src/assets/data/permissions';
import { ROLE_PRESETS } from 'src/dialogs/ManageUsersDialog/permissionOptions';
import type { TFunction } from 'i18next';
import i18next from 'i18next';
import { formatDatePickerValue } from 'src/utils/date-picker';
import { ErrorCode } from 'src/types/common';
import type { ITransaction, ISingleInvoiceResponse } from 'src/types/invoices';
import { PaymentTransactionType } from 'src/types/payments';
import { InvoiceStage } from 'src/types/invoices';

// Get initial
export const getInitials = (fullName: string): string => {
  const names = fullName.trim().split(/\s+/);

  const initials = names.map((x) => (x[0] || '').toUpperCase()).join('');

  return initials;
};

// Reformat the errors coming from the backend
export const reformatErrors = (errors?: FieldError[]): FormikErrors<any> => {
  const formikErrors: Record<string, any> = {};

  errors?.forEach((e) => {
    if (!formikErrors[e.fieldName]) {
      formikErrors[e.fieldName] = [];
    }

    formikErrors[e.fieldName].push(getErrorMessage(e));
  });

  return formikErrors;
};

const getErrorMessage = (error: FieldError): string => {
  // TODO: Figure out a better way to handle this case where we have very specific errors
  if (error.fieldName === 'tempPassword' && error.errorCode === 'invalid_string') {
    return i18next.t(`errors.tempPassword`);
  }

  if (error.fieldName === 'email' && error.errorCode === ErrorCode.CONFLICT) {
    return i18next.t(`errors.email_conflict`);
  }

  return `${i18next.t(`forms.labels.${error.fieldName}`)}: ${i18next.t(`errors.zod.${error.errorCode}`)}`;
};

// Slugify a string
export function slugify(text: string): string {
  return text
    .toLowerCase() // Convert to lowercase
    .trim() // Trim whitespace from both ends
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric characters with hyphens
    .replace(/(^-|-$)/g, ''); // Remove leading or trailing hyphens
}

export const buildURLQueryParams = <T>(currentFilters: T, prev: URLSearchParams) => {
  const previousEntries = prev && prev.entries && Object.fromEntries(prev.entries());

  const queryFilters: { [key: string]: string } = { ...previousEntries };

  for (const key in currentFilters) {
    const filterValue = currentFilters[key as keyof T];

    if (Array.isArray(filterValue)) {
      queryFilters[key] = filterValue.join(',');
    } else if (isObject(filterValue) && dayjs(filterValue as string).isValid()) {
      queryFilters[key] = formatDatePickerValue(dayjs(filterValue as string)) ?? '';
    } else if (!isObject(filterValue)) {
      queryFilters[key] = currentFilters[key as keyof T] as string;
    }
  }

  const result = removeBlankAttributes(queryFilters);

  return result;
};

function isObject(value: any): boolean {
  return typeof value === 'object' && value !== null;
}

function removeBlankAttributes(obj: any) {
  const result: any = {};

  for (const key in obj) {
    if (
      obj[key] !== null &&
      obj[key] !== undefined &&
      obj[key] !== '' &&
      obj[key] !== 0 &&
      obj[key] !== 'all'
    ) {
      result[key] = obj[key];
    }
  }

  return result;
}

export const getPercentage = (value: number, total: number): number => {
  if (total === 0) return 0; // Prevent division by zero
  return (value / total) * 100;
};

export const capitalizeFirstLetter = (value: string) => {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

export function camelCaseToNormal(str: string, capitaliseWords?: boolean): string {
  const replacedStr = str.replace(/([a-z])([A-Z])/g, '$1 $2');

  if (capitaliseWords) {
    return replacedStr.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return replacedStr;
}

export function camelCaseToKebap(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

export function underscoreToDash(str: string): string {
  if (str.includes('_')) {
    return str.replace(/_/g, '-');
  }
  return str;
}

export const getRandomEnumValue = <T extends Record<string, string | number>>(
  enumObj: T
): T[keyof T] => {
  const values = Object.values(enumObj).filter(
    (value) => typeof value === 'string' || typeof value === 'number'
  );

  return values[Math.floor(Math.random() * values.length)] as T[keyof T];
};

export const splitStringIfCamelCase = (str: string): string[] => {
  const regex = /(?<!^)(?=[A-Z])/;

  return str.split(regex).map(capitalizeFirstLetter);
};

const passwordChars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$';

// Generate a secure password that meets the following requirements:
// - At least 10 characters
// - Contains at least one uppercase letter
// - Contains at least one lowercase letter
// - Contains at least one number
// - Contains at least one special character
export const generatePassword = () => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const specials = '~!@-#$';

  // Ensure we have at least one of each required character type
  const randomChar = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
  const password = [
    randomChar(lowercase),
    randomChar(uppercase),
    randomChar(numbers),
    randomChar(specials),
    // Add 6 more random characters from all allowed characters
    ...Array(6)
      .fill(0)
      .map(() => passwordChars[Math.floor(Math.random() * passwordChars.length)]),
  ];

  // Shuffle the array to make the password more random
  for (let i = password.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join('');
};

export const hasAllPermissions = (userPermissions: string[] = []): boolean => {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  // Get all values from the Permission enum
  const allPermissions = Object.values(Permission);

  // Check if user has every permission from the enum
  return allPermissions.every((permission) => userPermissions.includes(permission));
};

// Find the current role based on permissions
export const permissionsToRole = (permissions: Permission[]) => {
  if (!permissions?.length) return 'custom';

  // Only return a role if there's an exact match between permissions and role's permissions
  const matchedRole = ROLE_PRESETS.find((role) => {
    if (role.id === 'custom') return false;

    return (
      role.permissions.length === permissions.length ||
      permissions.every((p) => role.permissions.includes(p))
    );
  });

  return matchedRole?.id || 'custom';
};

export const minAbs = (a: number | string, b: number | string): number => {
  const parsedA = Math.abs(Number(a));
  const parsedB = Math.abs(Number(b));

  if (isNaN(parsedA) || isNaN(parsedB)) {
    throw new Error('Invalid input: both values must be numbers or numeric strings.');
  }

  return Math.min(parsedA, parsedB);
};

export const isInPrecisionRange = (value: string, maxDecimalPlaces: number = 10): boolean => {
  const decimalPattern = new RegExp(`^-?\\d+(\\.\\d{${maxDecimalPlaces + 1},})$`);
  return !decimalPattern.test(value);
};

export const ensureIsArray = (value: string | string[] | undefined | null): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export const toNum = (v: unknown): number =>
  v === null || v === undefined || v === '' ? 0 : Number(v);

export const getCurrency = (invoice: ISingleInvoiceResponse | undefined): string => {
  return invoice?.currency || invoice?.bankAccount?.currency || 'EUR';
};

export const toKebabCase = (value: string): string => {
  return value
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_-]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
};

const TRANSACTION_TYPE_TO_TRANSLATION: Record<string, string> = {
  good_will: 'common.transactionTypes.good_will',
  credit_loss: 'common.transactionTypes.credit_loss',
  credit_amount: 'common.transactionTypes.credit_amount',
  direct_payment: 'common.transactionTypes.direct_payment',
  cancelled: 'common.transactionTypes.cancelled',
  bank: 'common.transactionTypes.bank',
  psp: 'common.transactionTypes.psp',
  direct_debit: 'common.transactionTypes.direct_debit',
  debt_collection: 'common.transactionTypes.debt_collection',
  reminder_fee_waiver: 'common.transactionTypes.reminder_fee_waiver',
  payment_to_dc: 'common.transactionTypes.payment_to_dc',
  credit_amount_to_dc: 'common.transactionTypes.credit_amount_to_dc',
  goodwill_waive_to_dc: 'common.transactionTypes.goodwill_waive_to_dc',
  payment_from_dc: 'common.transactionTypes.payment_from_dc',
  other_from_dc: 'common.transactionTypes.other_from_dc',
  to_surveillance: 'common.transactionTypes.to_surveillance',
  credit_loss_from_dc: 'common.transactionTypes.credit_loss_from_dc',
};

export const getTransactionTypeLabel = (type: string | null | undefined, t: TFunction): string => {
  if (!type) return t('common.na');
  const key = TRANSACTION_TYPE_TO_TRANSLATION[type];
  return key ? t(key) : t('common.na');
};

/** Manual/Auto plus "cause · initiator" for a reminder fee waiver row; null when there is no trigger (AS-6305). */
export const getWaiverTriggerLabels = (
  transaction: Pick<ITransaction, 'transactionType' | 'metadata'>,
  t: TFunction
): { isAuto: boolean; mode: string; cause: string } | null => {
  const trigger =
    transaction.transactionType === PaymentTransactionType.ReminderFeeWaiver
      ? transaction.metadata?.waiverTrigger
      : undefined;
  if (!trigger) return null;

  const isAuto = trigger.mode === 'auto';
  return {
    isAuto,
    mode: t(isAuto ? 'common.auto' : 'common.manual'),
    cause: `${t(`common.waiverTriggers.${trigger.source}`)} · ${trigger.initiatedBy}`,
  };
};

export const getInvoiceStageColor = (stage: string | null | undefined) =>
  (stage === InvoiceStage.Invoiced && 'info') ||
  (stage === InvoiceStage.Reminded && 'warning') ||
  (stage === InvoiceStage.DebtCollection && 'error') ||
  'default';
